import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { divisionsAPI } from '../services/divisionsApi';
import { adminTeamsAPI, adminSeasonsAPI } from '../services/adminApi';
import { getCantonName, getCantonEmoji } from '../utils/cantons';
import { CANTON_VARIATIONS } from '../services/apiRegistry';
import toast from 'react-hot-toast';
import AdminTeamMemberProfileCard, { AdminTeamMembersGrid } from './AdminTeamMemberProfileCard';

const CantonDetailContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  color: white;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    flex-wrap: wrap;
  }
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CantonCode = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Content = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 1px solid #e5e7eb;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #043663;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const DivisionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const DivisionCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #043663;
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
  }
`;

const DivisionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const DivisionName = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
`;

const TeamCount = styled.span`
  background: #dbeafe;
  color: #043663;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const DivisionDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SkillRange = styled.span`
  font-size: 0.875rem;
  color: #059669;
  font-weight: 500;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;

  &.active {
    background: #d1fae5;
    color: #065f46;
  }

  &.inactive {
    background: #fee2e2;
    color: #991b1b;
  }
`;

const ViewDetailsButton = styled.button`
  background: #043663;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 0.75rem;
  width: 100%;

  &:hover {
    background: #2563eb;
  }
`;

const TeamsSection = styled.div`
  margin-top: 2rem;
`;

const TeamsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;

  &:hover {
    background: #f9fafb;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #1f2937;
`;

const TeamName = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const TeamDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const DivisionBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #dbeafe;
  color: #043663;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const SkillLevel = styled.span`
  font-weight: 600;
  color: #059669;
`;

const ActionButton = styled.button`
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #374151;
  font-size: 0.75rem;
  cursor: pointer;
  margin-right: 0.5rem;

  &:hover {
    background: #f9fafb;
  }

  &.primary {
    background: #043663;
    color: white;
    border-color: #043663;

    &:hover {
      background: #032a4a;
    }
  }
`;

const LoadingContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #d52b1e;
`;

const EmptyContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

// Team Details Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const TeamInfoSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const TeamInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  .label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-size: 0.875rem;
    color: #1f2937;
    font-weight: 500;
  }
`;

const MembersSection = styled.div`
  margin-top: 2rem;
`;

const MembersTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const MemberCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const MemberHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MemberAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.125rem;
`;

const MemberBasicInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const MemberRole = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const MemberDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MemberDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MemberDetailLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const MemberDetailValue = styled.span`
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 500;
`;

const SkillLevelBadge = styled.span`
  background: #fef3c7;
  color: #92400e;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;


const AdminCantonDetail = ({ canton, seasonId: propSeasonId, onBack, onDivisionSelect }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamDetails, setShowTeamDetails] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(propSeasonId || '');
  const queryClient = useQueryClient();

  // Update selectedSeason if propSeasonId changes
  useEffect(() => {
    if (propSeasonId && propSeasonId !== selectedSeason) {
      setSelectedSeason(propSeasonId);
    }
  }, [propSeasonId, selectedSeason]);

  // Fetch seasons for season-aware queries
  useQuery(
    'admin-canton-seasons',
    () => adminSeasonsAPI.getAll(),
    {
      retry: false,
      onSuccess: (data) => {
        if (data.success && data.data.length > 0 && !selectedSeason) {
          // Prefer active season, then most recent
          const activeSeason = data.data.find(s => s.status === 'active');
          const seasonToSelect = activeSeason || data.data[0];
          setSelectedSeason(seasonToSelect.id);
        }
      },
      onError: (error) => {
        console.error('Error fetching seasons:', error);
      }
    }
  );

  // Fetch divisions for this canton - season-aware
  // Disable auto-refresh when viewing team details to prevent overwriting
  const { data: divisionsData, isLoading: divisionsLoading, error: divisionsError } = useQuery(
    ['cantonDivisions', canton, selectedSeason],
    () => divisionsAPI.getAll({ canton, season_id: selectedSeason || undefined, limit: 1000 }),
    {
      enabled: !!selectedSeason || true, // Allow without season_id for backward compatibility
      refetchInterval: showTeamDetails ? false : 60000 // Disabled when team details modal is open
    }
  );

  // Fetch teams for this canton - handle both code and name mapping, season-aware
  // Disable auto-refresh when viewing team details to prevent overwriting
  const { data: teamsData, isLoading: teamsLoading, error: teamsError } = useQuery(
    ['cantonTeams', canton, selectedSeason],
    () => {
      // Map canton code to possible team canton values (both proper names and lowercase)
      const searchTerms = CANTON_VARIATIONS[canton] || [canton];
      const params = { 
        canton: searchTerms.join(','), 
        season_id: selectedSeason || undefined,
        limit: 5000
      };
      console.log('🔍 API CALL DEBUG:');
      console.log('Canton:', canton);
      console.log('Season ID:', selectedSeason);
      console.log('Search terms:', searchTerms);
      console.log('API params:', params);
      return adminTeamsAPI.getAll(params);
    },
    {
      enabled: !!selectedSeason || true, // Allow without season_id for backward compatibility
      refetchInterval: showTeamDetails ? false : 60000 // Disabled when team details modal is open
    }
  );

  // Fetch detailed team data when a team is selected
  // Disable auto-refresh to prevent overwriting when viewing details
  const { data: teamDetailsData, isLoading: teamDetailsLoading } = useQuery(
    ['teamDetails', selectedTeam?.id, selectedSeason],
    () => adminTeamsAPI.getTeamDetails(selectedTeam?.id, { seasonId: selectedSeason || undefined }),
    {
      enabled: !!selectedTeam,
      refetchInterval: false, // Disabled - manual refresh only to prevent overwriting
      staleTime: 0, // Always consider data stale to force fresh fetch
      cacheTime: 0 // Don't cache to ensure fresh data
    }
  );

  // Assign teams mutation (kept for potential future use - automatic team assignment by canton)
  // eslint-disable-next-line no-unused-vars
  const assignTeamsMutation = useMutation(divisionsAPI.assignTeams, {
    onSuccess: () => {
      queryClient.invalidateQueries('cantonDivisions');
      queryClient.invalidateQueries('cantonTeams');
      toast.success('Teams assigned to divisions successfully!');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to assign teams');
    }
  });

  const handleViewTeamDetails = (team) => {
    setSelectedTeam(team);
    setShowTeamDetails(true);
    // Force refetch team details when modal opens
    setTimeout(() => {
      queryClient.invalidateQueries(['teamDetails', team.id]);
    }, 100);
  };

  const handleCloseTeamDetails = () => {
    setShowTeamDetails(false);
    setSelectedTeam(null);
  };

  // Lock body scroll when team details modal is open
  useEffect(() => {
    if (showTeamDetails) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      // Get the scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Lock the body scroll without affecting fixed elements like sidebar
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Also add padding to the sidebar to prevent it from shifting
      const sidebar = document.querySelector('aside');
      if (sidebar) {
        sidebar.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        // Restore scroll when modal closes
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        if (sidebar) {
          sidebar.style.paddingRight = '';
        }
        window.scrollTo(0, scrollY);
      };
    }
  }, [showTeamDetails]);


  if (divisionsLoading || teamsLoading) {
    return (
      <CantonDetailContainer>
        <LoadingContainer>Loading {getCantonName(canton)} data...</LoadingContainer>
      </CantonDetailContainer>
    );
  }

  if (divisionsError || teamsError) {
    return (
      <CantonDetailContainer>
        <ErrorContainer>
          Error loading {getCantonName(canton)} data: {divisionsError?.message || teamsError?.message}
        </ErrorContainer>
      </CantonDetailContainer>
    );
  }

  const divisions = divisionsData?.data?.divisions || [];
  const teams = teamsData?.data?.teams || [];
  
  // Filter divisions to only show those for the selected canton
  const filteredDivisions = divisions.filter(d => d.canton === canton);
  
  // Filter teams - backend already filters by division canton (playing canton), so we just need to filter disabled teams
  // Use season_is_active if available (season-aware), otherwise use is_active
  const filteredTeams = teams.filter(team => {
    // If season_is_active is available (season-aware), use it; otherwise use is_active
    const isTeamActive = team.season_is_active !== undefined ? team.season_is_active : team.is_active;
    return isTeamActive !== false; // Include teams that are active (true or undefined/null)
  });
  
  // CRITICAL: Use filteredTeams.length for total to match what's actually displayed
  // This includes both assigned teams (from divisions) and unassigned teams playing in this canton
  // The backend already filters by division canton (playing canton), so this is accurate
  const totalTeams = filteredTeams.length;
  
  // Calculate total members from filtered teams (for display in team list)
  const totalMembers = filteredTeams.reduce((sum, team) => sum + parseInt(team.member_count || 0), 0);
  const activeDivisions = filteredDivisions.filter(d => d.team_count > 0).length;
  const totalDivisions = filteredDivisions.length;

  return (
    <CantonDetailContainer>
      <Header>
        <BackButton onClick={onBack}>
          ← Back
        </BackButton>
        <HeaderContent>
          <Title>
            {getCantonEmoji(canton)} {getCantonName(canton)}
            <CantonCode>{canton}</CantonCode>
          </Title>
          <Subtitle>Division and team management for {getCantonName(canton)} canton</Subtitle>
        </HeaderContent>
      </Header>

      <Content>
        <StatsGrid>
          <StatCard>
            <StatNumber>{totalTeams}</StatNumber>
            <StatLabel>Total Teams</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{totalMembers}</StatNumber>
            <StatLabel>Total Players</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{totalDivisions}</StatNumber>
            <StatLabel>Divisions</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{activeDivisions}</StatNumber>
            <StatLabel>Active Divisions</StatLabel>
          </StatCard>
        </StatsGrid>

        <SectionTitle>Divisions ({totalDivisions})</SectionTitle>
        {filteredDivisions.length === 0 ? (
          <EmptyContainer>No divisions found for {getCantonName(canton)}.</EmptyContainer>
        ) : (
          <DivisionsGrid>
            {filteredDivisions.map((division) => (
            <DivisionCard key={division.id}>
              <DivisionHeader>
                <DivisionName>{division.name}</DivisionName>
                <TeamCount>{division.team_count} teams</TeamCount>
              </DivisionHeader>
              <DivisionDetails>
                <SkillRange>{division.skill_level_min} - {division.skill_level_max}</SkillRange>
                <StatusBadge className={division.is_active ? 'active' : 'inactive'}>
                  {division.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </DivisionDetails>
              {division.description && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  {division.description}
                </div>
              )}
              <ViewDetailsButton 
                onClick={(e) => {
                  e.stopPropagation();
                  onDivisionSelect({ ...division, seasonId: selectedSeason });
                }}
              >
                View Details
              </ViewDetailsButton>
            </DivisionCard>
          ))}
          </DivisionsGrid>
        )}

        <TeamsSection>
          <SectionTitle>Teams ({totalTeams})</SectionTitle>
          {filteredTeams.length === 0 ? (
            <EmptyContainer>No teams registered in {getCantonName(canton)} yet.</EmptyContainer>
          ) : (
            <TeamsTable>
              <TableHeader>
                <tr>
                  <TableHeaderCell style={{ width: '60px' }}>SR No</TableHeaderCell>
                  <TableHeaderCell>Team</TableHeaderCell>
                  <TableHeaderCell>Skill Level</TableHeaderCell>
                  <TableHeaderCell>Division</TableHeaderCell>
                  <TableHeaderCell>Members</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {filteredTeams.map((team, index) => (
                  <TableRow key={team.id}>
                    <TableCell style={{ textAlign: 'center', fontWeight: '600', color: '#6b7280' }}>
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <TeamName>{team.team_name}</TeamName>
                      <TeamDetails>Joined {new Date(team.created_at).toLocaleDateString()}</TeamDetails>
                    </TableCell>
                    <TableCell>
                      <SkillLevel>{team.team_skill_level || 'Not set'}</SkillLevel>
                    </TableCell>
                    <TableCell>
                      <DivisionBadge>{team.division_name || 'Unassigned'}</DivisionBadge>
                    </TableCell>
                    <TableCell>{team.member_count || 0}</TableCell>
                    <TableCell>
                      <ActionButton
                        type="button"
                        className="primary"
                        onClick={() => handleViewTeamDetails(team)}
                      >
                        View Details
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TeamsTable>
          )}
        </TeamsSection>
      </Content>

      {/* Team Details Modal */}
      {showTeamDetails && selectedTeam && (
        <ModalOverlay onClick={handleCloseTeamDetails}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Team Details - {selectedTeam.team_name}</ModalTitle>
              <CloseButton onClick={handleCloseTeamDetails}>×</CloseButton>
            </ModalHeader>

            <ModalBody>
              <TeamInfoSection>
                <TeamInfoGrid>
                  <InfoItem>
                    <div className="label">Team Name</div>
                    <div className="value">{selectedTeam.team_name}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Canton</div>
                    <div className="value">{selectedTeam.canton}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Skill Level</div>
                    <div className="value">
                      <SkillLevelBadge>{selectedTeam.team_skill_level}</SkillLevelBadge>
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Division</div>
                    <div className="value">
                      <DivisionBadge>{teamDetailsData?.data?.team?.division_name || 'Not assigned'}</DivisionBadge>
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Member Count</div>
                    <div className="value">{selectedTeam.member_count}/2</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Team Phone Number</div>
                    <div className="value">
                      {teamDetailsData?.data?.team?.phone_number || 'Not provided'}
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Joined</div>
                    <div className="value">
                      {selectedTeam.created_at ? new Date(selectedTeam.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </InfoItem>
                </TeamInfoGrid>
              </TeamInfoSection>

              <MembersSection>
                <MembersTitle>Team Members</MembersTitle>
                {teamDetailsLoading ? (
                  <LoadingContainer>Loading team members...</LoadingContainer>
                ) : teamDetailsData?.data?.members ? (
                  <AdminTeamMembersGrid>
                    {teamDetailsData.data.members.map((member) => (
                      <AdminTeamMemberProfileCard
                        key={member.id}
                        member={member}
                        teamId={selectedTeam?.id}
                        seasonId={selectedSeason || undefined}
                        onPaymentUpdated={() =>
                          queryClient.invalidateQueries(['teamDetails', selectedTeam?.id])
                        }
                      />
                    ))}
                  </AdminTeamMembersGrid>
                ) : (
                  <EmptyContainer>
                    No team members found
                  </EmptyContainer>
                )}
              </MembersSection>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </CantonDetailContainer>
  );
};

export default AdminCantonDetail;
